import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Download } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../../lib/api";
import {
  downloadServiceExport,
  exportServiceTickets,
} from "../../lib/serviceApi";
import {
  SERVICE_BRANCHES,
  SERVICE_BRANCH_LABELS,
  SERVICE_CATEGORIES,
  SERVICE_CATEGORY_LABELS,
  SERVICE_STAGES,
  SERVICE_STAGE_LABELS,
} from "../../lib/serviceConstants";
import { Button } from "../ui/button";
import { FieldFeedback, FormError } from "../ui/feedback";
import { FlowpiDialog } from "../ui/flowpi-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

export const SERVICE_EXPORT_CATEGORY_VALUES = Object.freeze(Object.values(SERVICE_CATEGORIES));
export const SERVICE_EXPORT_BRANCH_VALUES = Object.freeze(Object.values(SERVICE_BRANCHES));
export const SERVICE_EXPORT_STAGE_VALUES = Object.freeze(Object.values(SERVICE_STAGES));

function canonicalSelection(values, canonicalValues) {
  const selected = new Set(Array.isArray(values) ? values : []);
  return canonicalValues.filter((value) => selected.has(value));
}

export function buildServiceExportPayload({
  scope,
  currentCategory,
  currentBranch,
  currentStage,
  appliedSearch,
  categories,
  branches,
  stages,
  dateInFrom,
  dateInTo,
}) {
  if (scope === "data_shown") {
    const payload = {
      scope: "data_shown",
      category: currentCategory,
      branch: currentBranch,
      stage: currentStage,
    };
    const search = String(appliedSearch || "").trim();
    if (search) payload.search = search;
    return payload;
  }

  const payload = {
    scope: "filters",
    categories: canonicalSelection(categories, SERVICE_EXPORT_CATEGORY_VALUES),
    branches: canonicalSelection(branches, SERVICE_EXPORT_BRANCH_VALUES),
    stages: canonicalSelection(stages, SERVICE_EXPORT_STAGE_VALUES),
  };
  if (dateInFrom) payload.date_in_from = dateInFrom;
  if (dateInTo) payload.date_in_to = dateInTo;
  return payload;
}

function MultiSelectField({
  name,
  label,
  values,
  labels,
  selected,
  disabled,
  error,
  onChange,
  open,
  onOpenChange,
}) {
  const allSelected = selected.length === values.length;
  const errorId = `service-export-${name}-error`;
  const menuId = `service-export-${name}-options`;

  const toggle = (value) => {
    const next = selected.includes(value)
      ? selected.filter((item) => item !== value)
      : [...selected, value];
    onChange(canonicalSelection(next, values));
  };

  return (
    <div className="relative">
      <label className="text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-600 dark:text-zinc-300">
        {label}
      </label>
      <Popover open={open && !disabled} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label={`${label} selection`}
            aria-expanded={open && !disabled}
            aria-controls={menuId}
            aria-describedby={error ? errorId : undefined}
            className="mt-2 flex w-full items-center justify-between rounded-sm border border-zinc-300 bg-white px-3 py-2.5 text-left text-sm text-zinc-900 outline-none transition-colors hover:border-primary focus:border-primary focus:ring-2 focus:ring-primary/10 disabled:cursor-not-allowed disabled:bg-zinc-100 disabled:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:disabled:bg-zinc-950"
            data-testid={`service-export-${name}-trigger`}
          >
            <span>{selected.length === values.length ? "All selected" : `${selected.length} selected`}</span>
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          id={menuId}
          align="start"
          collisionPadding={16}
          className="w-[var(--radix-popover-trigger-width)] rounded-sm border-zinc-200 bg-white p-2 shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
          data-testid={`service-export-${name}-options`}
        >
          <label className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-2 text-sm font-semibold hover:bg-zinc-50 dark:hover:bg-zinc-800">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={() => onChange(allSelected ? [] : [...values])}
              aria-label={`Select all ${label}`}
            />
            Select All
          </label>
          <div className="my-1 border-t border-zinc-200 dark:border-zinc-700" />
          {values.map((value) => (
            <label key={value} className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-2 text-sm hover:bg-zinc-50 dark:hover:bg-zinc-800">
              <input
                type="checkbox"
                checked={selected.includes(value)}
                onChange={() => toggle(value)}
                aria-label={`${label}: ${labels[value]}`}
              />
              {labels[value]}
            </label>
          ))}
        </PopoverContent>
      </Popover>
      <FieldFeedback id={errorId}>{error}</FieldFeedback>
    </div>
  );
}

export default function ServiceExportDialog({
  open,
  currentCategory,
  currentBranch,
  currentStage,
  appliedSearch,
  onClose,
}) {
  const [scope, setScope] = useState("filters");
  const [categories, setCategories] = useState([...SERVICE_EXPORT_CATEGORY_VALUES]);
  const [branches, setBranches] = useState([...SERVICE_EXPORT_BRANCH_VALUES]);
  const [stages, setStages] = useState([...SERVICE_EXPORT_STAGE_VALUES]);
  const [dateInFrom, setDateInFrom] = useState("");
  const [dateInTo, setDateInTo] = useState("");
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const requestInFlightRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setScope("filters");
    setCategories([...SERVICE_EXPORT_CATEGORY_VALUES]);
    setBranches([...SERVICE_EXPORT_BRANCH_VALUES]);
    setStages([...SERVICE_EXPORT_STAGE_VALUES]);
    setDateInFrom("");
    setDateInTo("");
    setErrors({});
    setSubmitError("");
    setSubmitting(false);
    setOpenDropdown(null);
    requestInFlightRef.current = false;
  }, [open]);

  if (!open) return null;

  const clearErrors = (field) => {
    setSubmitError("");
    if (field) setErrors((current) => ({ ...current, [field]: "", dateRange: field.startsWith("date") ? "" : current.dateRange }));
  };

  const close = () => {
    if (requestInFlightRef.current) return;
    setSubmitError("");
    onClose?.();
  };

  const validate = () => {
    if (scope !== "filters") return {};
    const nextErrors = {};
    if (categories.length === 0) nextErrors.categories = "Select at least one Category.";
    if (branches.length === 0) nextErrors.branches = "Select at least one Branch.";
    if (stages.length === 0) nextErrors.stages = "Select at least one Stage.";
    if (dateInFrom && dateInTo && dateInFrom > dateInTo) {
      nextErrors.dateRange = "Date In To must be on or after Date In From.";
    }
    return nextErrors;
  };

  const submit = async () => {
    if (requestInFlightRef.current) return;
    const nextErrors = validate();
    setErrors(nextErrors);
    setSubmitError("");
    if (Object.keys(nextErrors).length > 0) return;

    requestInFlightRef.current = true;
    setSubmitting(true);
    try {
      const payload = buildServiceExportPayload({
        scope,
        currentCategory,
        currentBranch,
        currentStage,
        appliedSearch,
        categories,
        branches,
        stages,
        dateInFrom,
        dateInTo,
      });
      const exportFile = await exportServiceTickets(payload);
      downloadServiceExport(exportFile);
      toast.success("Service export downloaded");
      onClose?.();
    } catch (error) {
      setSubmitError(formatApiError(error));
    } finally {
      requestInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  const filterDisabled = scope !== "filters" || submitting;
  const handleDropdownOpenChange = (name) => (nextOpen) => {
    setOpenDropdown((current) => {
      if (nextOpen) return name;
      return current === name ? null : current;
    });
  };

  const footer = (
    <>
      <Button type="button" onClick={close} disabled={submitting} variant="cancel" data-action-category="cancel">Cancel</Button>
      <Button type="button" onClick={submit} loading={submitting} loadingLabel="Exporting..." data-testid="service-export-submit" variant="export" data-action-category="export-download">
        <Download className="h-4 w-4" aria-hidden="true" />
        Export
      </Button>
    </>
  );

  return (
    <FlowpiDialog
      open={open}
      onClose={close}
      busy={submitting}
      size="form"
      eyebrow="Service Export"
      title="Export Service"
      closeLabel="Close Export Service"
      footer={footer}
      testId="service-export-modal"
    >
      <div className="space-y-5">
          <fieldset disabled={submitting}>
            <legend className="text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-600 dark:text-zinc-300">Export scope</legend>
            <div className="mt-2 flex flex-wrap gap-5" role="radiogroup" aria-label="Export scope">
              {[
                ["data_shown", "By Data Shown"],
                ["filters", "By Filters"],
              ].map(([value, label]) => (
                <label key={value} className="flex cursor-pointer items-center gap-2 text-sm text-zinc-800 dark:text-zinc-200">
                  <input
                    type="radio"
                    name="service-export-scope"
                    value={value}
                    checked={scope === value}
                    disabled={submitting}
                    onChange={() => {
                      setScope(value);
                      setOpenDropdown(null);
                      setErrors({});
                      setSubmitError("");
                    }}
                  />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>

          <div className="grid gap-4 sm:grid-cols-3">
            <MultiSelectField name="categories" label="Category" values={SERVICE_EXPORT_CATEGORY_VALUES} labels={SERVICE_CATEGORY_LABELS} selected={categories} disabled={filterDisabled} error={errors.categories} onChange={(value) => { setCategories(value); clearErrors("categories"); }} open={openDropdown === "categories"} onOpenChange={handleDropdownOpenChange("categories")} />
            <MultiSelectField name="branches" label="Branch" values={SERVICE_EXPORT_BRANCH_VALUES} labels={SERVICE_BRANCH_LABELS} selected={branches} disabled={filterDisabled} error={errors.branches} onChange={(value) => { setBranches(value); clearErrors("branches"); }} open={openDropdown === "branches"} onOpenChange={handleDropdownOpenChange("branches")} />
            <MultiSelectField name="stages" label="Stage" values={SERVICE_EXPORT_STAGE_VALUES} labels={SERVICE_STAGE_LABELS} selected={stages} disabled={filterDisabled} error={errors.stages} onChange={(value) => { setStages(value); clearErrors("stages"); }} open={openDropdown === "stages"} onOpenChange={handleDropdownOpenChange("stages")} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="service-export-date-from" className="text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-600 dark:text-zinc-300">Date In From</label>
              <input id="service-export-date-from" type="date" value={dateInFrom} onChange={(event) => { setDateInFrom(event.target.value); clearErrors("dateInFrom"); }} disabled={filterDisabled} className="mt-2 w-full rounded-sm border border-zinc-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 disabled:cursor-not-allowed disabled:bg-zinc-100 disabled:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:disabled:bg-zinc-950" />
            </div>
            <div>
              <label htmlFor="service-export-date-to" className="text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-600 dark:text-zinc-300">Date In To</label>
              <input id="service-export-date-to" type="date" value={dateInTo} onChange={(event) => { setDateInTo(event.target.value); clearErrors("dateInTo"); }} disabled={filterDisabled} aria-describedby={errors.dateRange ? "service-export-date-error" : undefined} className="mt-2 w-full rounded-sm border border-zinc-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 disabled:cursor-not-allowed disabled:bg-zinc-100 disabled:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:disabled:bg-zinc-950" />
              <FieldFeedback id="service-export-date-error">{errors.dateRange}</FieldFeedback>
            </div>
          </div>

          <FormError data-testid="service-export-error">{submitError}</FormError>
      </div>
    </FlowpiDialog>
  );
}
